"use client"

import { useEffect, useState, useCallback } from "react"
import { supabase } from "../../supabaseClient"
import { useAuth } from "../../context/AuthContext"
import { applyInstitutionFilter } from "../../lib/tenant"
import "../../styles/ProfessorList.css"

const DIAS_LABELS = {
  seg: "Segunda",
  ter: "Terça",
  qua: "Quarta",
  qui: "Quinta",
  sex: "Sexta",
  sab: "Sábado",
}

export default function ProfessorList({ keyProp, onEdit }) {
  const { isAdmin, instituicaoId } = useAuth()

  const [professores, setProfessores] = useState([])
  const [loading, setLoading] = useState(true)

  const fetchProfessores = useCallback(async () => {
    setLoading(true)

    try {
      let query = supabase
        .from("professores")
        .select(`
          id,
          instituicao_id,
          nome,
          preferencia_periodo,
          disponibilidade,
          departamento:departamento_id (id, nome),
          curso:curso_id (id, nome),
          created_at
        `)
        .order("nome", { ascending: true })

      query = applyInstitutionFilter(query, isAdmin, instituicaoId)

      const { data, error } = await query

      if (error) {
        console.error("Erro Supabase:", error)
        throw error
      }

      console.debug("Professores carregados:", data?.length || 0)

      // Carregar disciplinas e turmas vinculadas para cada professor
      const profsComVinculos = await Promise.all(
        (data || []).map(async (p) => {
          // Buscar disciplinas
          let discQuery = supabase
            .from("professor_disciplina")
            .select("disciplina:disciplina_id(id, nome, codigo)")
            .eq("professor_id", p.id)
          discQuery = applyInstitutionFilter(discQuery, isAdmin, instituicaoId)
          const { data: discData, error: discErr } = await discQuery
          if (discErr) console.error("Erro ao carregar disciplinas de", p.id, discErr)

          // Buscar turmas
          let turmasQuery = supabase
            .from("professor_turma")
            .select("turma:turma_id(id, nome)")
            .eq("professor_id", p.id)
          turmasQuery = applyInstitutionFilter(turmasQuery, isAdmin, instituicaoId)
          const { data: turmasData, error: turmasErr } = await turmasQuery
          if (turmasErr) console.error("Erro ao carregar turmas de", p.id, turmasErr)

          return {
            ...p,
            disciplinas: (discData || []).map((d) => d.disciplina).filter(Boolean),
            turmas: (turmasData || []).map((t) => t.turma).filter(Boolean),
            preferencia_periodo: Array.isArray(p.preferencia_periodo)
              ? p.preferencia_periodo
              : p.preferencia_periodo
              ? [p.preferencia_periodo]
              : [],
            disponibilidade:
              typeof p.disponibilidade === "string"
                ? safeJSONParse(p.disponibilidade)
                : p.disponibilidade || {},
          }
        })
      )

      console.debug("Professores com vínculações:", profsComVinculos.length)
      setProfessores(profsComVinculos)
    } catch (err) {
      console.error("Erro ao buscar professores:", err.message)
      alert("Erro ao carregar professores: " + err.message)
    } finally {
      setLoading(false)
    }
  }, [isAdmin, instituicaoId])

  useEffect(() => {
    fetchProfessores()
  }, [keyProp, isAdmin, instituicaoId, fetchProfessores])

  useEffect(() => {
    const channel = supabase.channel("realtime-professores")

    ;["professores"].forEach((table) => {
      channel.on(
        "postgres_changes",
        { event: "*", schema: "public", table },
        () => {
          fetchProfessores()
        }
      )
    })

    channel.subscribe()

    return () => {
      supabase.removeChannel(channel)
    }
  }, [])

  function safeJSONParse(value) {
    try {
      return JSON.parse(value)
    } catch {
      return {}
    }
  }

  const handleDelete = async (professor) => {
    if (!isAdmin && professor.instituicao_id !== instituicaoId) {
      alert("Você não tem permissão para excluir este professor.")
      return
    }

    if (!window.confirm(`Tem certeza que deseja excluir o professor "${professor.nome}"?`)) {
      return
    }

    try {
      await supabase
        .from("professor_disciplina")
        .delete()
        .eq("professor_id", professor.id)

      await supabase
        .from("professor_turma")
        .delete()
        .eq("professor_id", professor.id)

      await supabase
        .from("horarios")
        .update({ professor_id: null })
        .eq("professor_id", professor.id)

      let deleteQuery = supabase.from("professores").delete().eq("id", professor.id)
      if (!isAdmin && instituicaoId) {
        deleteQuery = deleteQuery.eq("instituicao_id", instituicaoId)
      }
      const { error } = await deleteQuery

      if (error) throw error

      alert("Professor excluído com sucesso!")
      fetchProfessores()
    } catch (err) {
      console.error("Erro ao excluir professor:", err.message)
      alert("Erro ao excluir professor: " + err.message)
    }
  }

  const formatarDisponibilidade = (disp) => {
    if (!disp || typeof disp !== "object") return null

    const dias = Object.entries(disp).filter(([_, horarios]) => {
      return Array.isArray(horarios) && horarios.length >= 2
    })

    if (dias.length === 0) return null

    return dias.map(([dia, horarios]) => ({
      dia: DIAS_LABELS[dia] || dia,
      entrada: horarios[0],
      saida: horarios[1],
    }))
  }

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Carregando professores...</p>
      </div>
    )
  }

  return (
    <div className="professor-list-container">
      <h3 className="list-title">Professores Cadastrados</h3>

      {professores.length === 0 ? (
        <div className="empty-state">
          <p>Nenhum professor cadastrado.</p>
        </div>
      ) : (
        <div className="professor-list">
          {professores.map((prof) => {
            const disp = formatarDisponibilidade(prof.disponibilidade)

            return (
              <div key={prof.id} className="professor-card">

                <div className="card-header">
                  <h3 className="professor-nome">{prof.nome}</h3>
                </div>

                <div className="card-body">

                  {prof.preferencia_periodo.length > 0 && (
                    <div className="info-row">
                      <span className="info-label">Preferência:</span>
                      <span className="info-value">
                        {prof.preferencia_periodo
                          .map((p) =>
                            p === "manha"
                              ? "Manhã"
                              : p === "tarde"
                              ? "Tarde"
                              : "Noite"
                          )
                          .join(", ")}
                      </span>
                    </div>
                  )}

                  {prof.departamento?.nome && (
                    <div className="info-row">
                      <span className="info-label">Departamento:</span>
                      <span className="info-value">{prof.departamento.nome}</span>
                    </div>
                  )}

                  {prof.curso?.nome && (
                    <div className="info-row">
                      <span className="info-label">Curso:</span>
                      <span className="info-value">{prof.curso.nome}</span>
                    </div>
                  )}

                  {prof.disciplinas && prof.disciplinas.length > 0 && (
                    <div className="disciplinas-section">
                      <span className="info-label">Disciplinas:</span>
                      <div className="disciplinas-list">
                        {prof.disciplinas.map((d) => (
                          <span key={d.id} className="disciplina-badge">
                            {d.nome}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {prof.turmas && prof.turmas.length > 0 && (
                    <div className="turmas-section">
                      <span className="info-label">Turmas:</span>
                      <div className="turmas-list">
                        {prof.turmas.map((t) => (
                          <span key={t.id} className="turma-badge">
                            {t.nome}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {disp && (
                    <div className="disponibilidade-section">
                      <span className="info-label">Disponibilidade:</span>

                      {disp.map((d, i) => (
                        <div key={i} className="disp-item">
                          <span className="disp-dia">{d.dia}:</span>
                          <span className="disp-horario">
                            {d.entrada} - {d.saida}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}

                </div>

                <div className="card-actions">
                  <button
                    className="btn-editar"
                    onClick={() => {
                      if (!isAdmin && prof.instituicao_id !== instituicaoId) {
                        alert("Você não tem permissão para editar este professor.")
                        return
                      }
                      onEdit && onEdit(prof)
                    }}
                  >
                    Editar
                  </button>

                  <button
                    className="btn-excluir"
                    onClick={() => handleDelete(prof)}
                  >
                    Excluir
                  </button>
                </div>

              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}